import { Popover, useOpenable } from '@execonline-inc/execonline-ui';
import { flip } from '@floating-ui/react';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo, useCallback } from 'react';
import ProgramExperienceCard from '.';
import {
  calculatePlacementOffset,
  useProgramPopoverPlacement,
} from '../../../../../../../ChatbotPreview/Previewer/Ready/Details/DefaultSection/PopoverContent/Types';
import DisplayPopover from '../../../../../../../ChatbotPreview/Section/PopoverContent/DisplayPopover';
import { ExperienceResource } from '../../../../../Types';
import { useLearningCollection } from '../../../../../LearningCollectionContext';
import EditStartDatesModal from '../../../../../EditStartDatesModal';

export interface Props {
  experienceResource: ExperienceResource;
  sectionIndex: number;
  // DVLP-7601: preview mode passes false so the Scheduled-Course card
  // suppresses the Edit-Dates pencil.
  editable?: boolean;
}

function PopoverContent({ experienceResource, sectionIndex, editable = true }: Props) {
  const placement = useProgramPopoverPlacement();
  const middleware = useMemo(() => [calculatePlacementOffset(placement), flip()], [placement]);
  const { isOpen, onOpen: openEditDatesModal, onClose: closeEditDatesModal } = useOpenable();

  // Get scheduled programs and handler from context
  const { resourceState, handleScheduledProgramToggle } = useLearningCollection();
  const experienceId = experienceResource.payload.id;

  // Find the scheduled program for this experience and get selected program IDs as a Set
  const selectedDateIds = useMemo(() => {
    const scheduledProgram = resourceState.payload.scheduled_programs?.find(
      (sp) => sp.id === experienceId,
    );
    return scheduledProgram ? new Set(scheduledProgram.program_ids) : undefined;
  }, [resourceState.payload.scheduled_programs, experienceId]);

  // Create a handler that wraps the context handler with the correct experienceId
  const onDateSelectionChange = useCallback(
    (programId: number, selected: boolean) => {
      handleScheduledProgramToggle(experienceId, programId, selected);
    },
    [handleScheduledProgramToggle, experienceId],
  );

  return (
    <>
      <Popover
        className="max-w-[20.625rem] text-foreground"
        trigger="hover"
        delay={{ open: 100, close: 0 }}
        returnFocus={false}
        placement="top"
        middleware={middleware}
        renderTrigger={(ref, getReferenceProps) => {
          const { 'aria-expanded': _, 'aria-haspopup': __, ...divProps } = getReferenceProps();
          return (
            <div ref={ref} {...divProps}>
              <ProgramExperienceCard
                experienceResource={experienceResource}
                sectionIndex={sectionIndex}
                selectedDateIds={selectedDateIds}
                onEditDates={editable ? openEditDatesModal : undefined}
              />
            </div>
          );
        }}
        onClose={({ refs }) => {
          fromNullable(refs.domReference.current).do((element) => {
            element.focus({ preventScroll: true });
          });
        }}
      >
        <DisplayPopover experienceResource={experienceResource} />
      </Popover>
      <EditStartDatesModal
        isOpen={isOpen}
        close={closeEditDatesModal}
        experienceResource={experienceResource}
        selectedDateIds={selectedDateIds ?? new Set()}
        onSelectionChange={onDateSelectionChange}
      />
    </>
  );
}

export default observer(PopoverContent);
